import React, { useContext, useMemo } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { MainContext } from "../App";
import { dialogPaperSx, dialogTitleSx } from "../@shared/ui/tokens";

/**
 * Hard-block modal to prevent non-test roles from using the DEV server.
 *
 * Allowed roles: play_tester, manager, admin
 * DEV host: infinft-royale.web.app (and optionally localhost)
 * PROD host: infinftroyale.com
 */
export default function ServerGateModal({
  open,
  currentHost,
  redirectUrl,
  allowedRoles = ["play_tester", "manager", "admin"],
  userRoles = [],
}) {
  const { Theme } = useContext(MainContext);
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("md"));

  const hasAllowedRole = useMemo(() => {
    const roles = Array.isArray(userRoles) ? userRoles : [];
    return roles.some((r) => allowedRoles.includes(r));
  }, [userRoles, allowedRoles]);

  // If somehow opened but user is allowed, don't show.
  if (!open || hasAllowedRole) return null;

  const handleGo = () => {
    // hard redirect
    window.location.href = redirectUrl;
  };

  return (
    <Dialog
      open={true}
      onClose={() => {
        // intentionally do nothing (hard block)
      }}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          ...dialogPaperSx,
          border: "1px solid rgba(255,255,255,0.10)",
        },
      }}
    >
      <DialogTitle
        sx={dialogTitleSx}
      >
        Wrong Server
      </DialogTitle>

      <DialogContent
        sx={{
          background:
            "radial-gradient(circle at top, #3b1f8a33 0%, transparent 55%)",
          p: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            minHeight: isMobile ? "40vh" : "32vh",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2.5,
              border: "1px solid rgba(255,255,255,0.10)",
              background: "rgba(0,0,0,0.25)",
            }}
          >
            <Typography sx={{ color: "#fff", fontWeight: 900, mb: 1 }}>
              This environment is restricted
            </Typography>

            <Typography sx={{ color: "#bdbdde", fontSize: 14, mb: 1 }}>
              You’re currently on the <b>development server</b>, which is only for
              Play Testers, Managers, and Admins.
            </Typography>

            <Box sx={{ mt: 1 }}>
              <Typography sx={{ color: "#fff", fontSize: 13, fontWeight: 800 }}>
                Current:
              </Typography>
              <Typography sx={{ color: "#bdbdde", fontSize: 13 }}>
                {currentHost}
              </Typography>

              <Typography
                sx={{ color: "#fff", fontSize: 13, fontWeight: 800, mt: 1 }}
              >
                Correct:
              </Typography>
              <Typography sx={{ color: "#bdbdde", fontSize: 13 }}>
                {redirectUrl}
              </Typography>
            </Box>
          </Box>

          <Button
            onClick={handleGo}
            variant="contained"
            sx={{
              py: 1.25,
              fontWeight: 900,
              borderRadius: 2,
              textTransform: "none",
              background: Theme?.palette?.secondary?.main || "#ffd24d",
              color: "#12062a",
              "&:hover": { opacity: 0.95 },
            }}
          >
            Go to Live Server
          </Button>

          <Typography sx={{ color: "#7c7aa8", fontSize: 12, textAlign: "center" }}>
            If you believe this is a mistake, contact an admin to get the
            <b> play_tester</b> role.
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
